import {
  Button,
  Card,
  Col,
  Container,
  Flex_unstable as Flex,
  Row,
  WhatsappIcon,
} from '@farol-ds/react'
import { useOnScreen } from '@jusbrasil-web/shared-hooks'
import { useUserSession } from '@jusbrasil-web/shared-user-session'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { useEffect, useMemo, useRef } from 'react'

import { WHATSAPP_URL } from '../../constants'
import styles from './seus-direitos-cta.module.scss'
import { VariantA, VariantB, VariantC } from './variants'

export interface SeusDireitosCtaProps {
  /** Search term (e.g. URL q param). Shown highlighted in the description. */
  searchTerm: string
  /** Experiment variant (variantA, variantB or variantC). */
  variant?: string
  /** Experiment ID for metrics. Optional; when not provided, it is not sent. */
  experimentId?: string
  /** Callback for variant B "explore" button. When not provided, the section is not shown. */
  onExploreClick?: () => void
  /** Label for variant B "explore" button. Default: variant B's DEFAULT_EXPLORE_BUTTON_LABEL. */
  exploreButtonLabel?: string
  /** Value for metadata.page in sendMetric events. Default: 'not_found'. */
  page?: string
}

const DEFAULT_TITLE = 'Parece que você tem uma dúvida jurídica'
const DEFAULT_DESCRIPTION = 'Envie a sua dúvida no WhatsApp para entender'
const DEFAULT_BUTTON_LABEL = 'Enviar dúvida no WhatsApp'

const VARIANT_C_DESCRIPTION =
  'O nosso assistente jurídico de perguntas e respostas é exclusivo do WhatsApp.'
const VARIANT_C_BUTTON_LABEL = 'Acessar resposta no WhatsApp'

const DEFAULT_WHATSAPP_MESSAGE = 'Olá! tenho uma dúvida jurídica'

const WHATSAPP_MESSAGE_PREFIX: Record<string, string> = {
  variantA: 'Minha dúvida é: ',
  variantB: 'Quero entender: ',
  variantC: 'Gostaria de saber: ',
}

export function SeusDireitosCta({
  searchTerm,
  variant,
  experimentId,
  onExploreClick,
  exploreButtonLabel,
  page,
}: SeusDireitosCtaProps) {
  const isVariantA = variant === 'variantA'
  const isVariantB = variant === 'variantB'
  const isVariantC = variant === 'variantC'

  const ctaRef = useRef<HTMLButtonElement>(null)
  const ctaViewSentRef = useRef(false)
  const session = useUserSession()
  const isCtaOnScreen = useOnScreen(ctaRef, { threshold: 0.5 }, false)
  const buttonLabel = isVariantC ? VARIANT_C_BUTTON_LABEL : DEFAULT_BUTTON_LABEL

  const ctaMetricsPayload = useMemo(
    () => ({
      app_id: 'web-search',
      metadata: {
        page: page ?? 'not_found',
        action: 'open_whatsapp_chat',
        cta_id: 'not_found_whatsapp_entry',
        destination: 'whatsapp',
        copy: buttonLabel,
        ...(experimentId != null && { experiment_id: experimentId }),
        variant: variant ?? undefined,
        channel: 'web_entrypoint',
      },
    }),
    [buttonLabel, variant, experimentId, page]
  )

  useEffect(() => {
    if (isCtaOnScreen && !ctaViewSentRef.current) {
      sendMetrics('SeusDireitos.CtaView', session?.pid?.toString() ?? null, ctaMetricsPayload)
      ctaViewSentRef.current = true
    }
  }, [isCtaOnScreen, ctaMetricsPayload, session?.pid])

  const onWhatsAppClick = () => {
    sendMetrics('SeusDireitos.CtaClick', session?.pid?.toString() ?? null, ctaMetricsPayload)
    const trimmed = searchTerm.trim()
    const prefix = variant ? WHATSAPP_MESSAGE_PREFIX[variant] : undefined
    const message = trimmed ? (prefix ? `${prefix}${trimmed}` : trimmed) : DEFAULT_WHATSAPP_MESSAGE
    const url = `${WHATSAPP_URL}?text=${encodeURIComponent(message)}`

    window.location.href = url
  }

  const buttonContent = (
    <Button
      ref={ctaRef}
      kind="primary"
      size="md"
      leftIcon={<WhatsappIcon />}
      block
      onClick={onWhatsAppClick}
    >
      {buttonLabel}
    </Button>
  )

  return (
    <Flex direction="column" align="center" className={styles.seusDireitosCta}>
      <Container>
        <Row>
          <Col xs={0} md={2} />
          <Col xs={12} md={8}>
            <Card
              className={
                isVariantC
                  ? styles.cardVariantC
                  : isVariantA || isVariantB
                  ? styles.cardVariantAB
                  : undefined
              }
            >
              {isVariantA && (
                <VariantA
                  title={DEFAULT_TITLE}
                  description={DEFAULT_DESCRIPTION}
                  searchParam={searchTerm}
                  buttonContent={buttonContent}
                />
              )}
              {isVariantB && (
                <VariantB
                  title={DEFAULT_TITLE}
                  description={DEFAULT_DESCRIPTION}
                  searchParam={searchTerm}
                  buttonContent={buttonContent}
                  onExploreClick={onExploreClick}
                  exploreButtonLabel={exploreButtonLabel}
                  experimentId={experimentId}
                  page={page}
                />
              )}
              {isVariantC && (
                <VariantC
                  title={DEFAULT_TITLE}
                  description={VARIANT_C_DESCRIPTION}
                  buttonContent={buttonContent}
                />
              )}
            </Card>
          </Col>
          <Col xs={0} md={2} />
        </Row>
      </Container>
    </Flex>
  )
}
